import React from 'react';
import { Stack, Text } from '@moda/om';
import { PageMeta } from '../../components/PageMeta';
import { Link } from '../../components/Link';
import './PrivacyApplicantCandidatePage.scss';

export const PrivacyApplicantCandidatePage = () => (
  <>
    <PageMeta title="Privacy Applicant Candidate" />

    <div className="PrivacyApplicantCandidatePage">
      <Stack className="PrivacyApplicantCandidatePage__title" space={4}>
        <Text treatment="h3" family="serif">
          Applicant and Candidate Privacy Policy
        </Text>
        <Text treatment="body2" family="serif">
          This Applicant and Candidate Privacy Policy (“policy”) explains what information Moda
          Operandi, Inc. (together with its subsidiaries and affiliates, “Moda”, “we”, or “our”) may
          collect during our application and recruitment process, why we collect it, how we use that
          information, and how to access and update that information.
        </Text>
        <Text treatment="body2" family="serif">
          Please note that your use of any recruitment tools and services are governed by any
          applicable terms and privacy policies, as well as our general{' '}
          <Link className="TermsAndConditionsPage__link" to="/privacy">
            PRIVACY POLICY
          </Link>
          .
        </Text>
      </Stack>

      <Stack className="PrivacyApplicantCandidatePage__content" space={4}>
        <Text treatment="h5" family="serif">
          Information We Collect:
        </Text>
        <Text treatment="body2" family="serif">
          This policy covers the information you share with us and/or which may be acquired or
          produced by us or on our behalf during the application or recruitment process, including:
        </Text>
        <Text treatment="body2" family="serif">
          <ul className="PrivacyApplicantCandidatePage__list">
            <li>
              Your name, address, email address, telephone number, and other contact information;
            </li>
            <li>
              Your resume or CV, cover letter, previous and/or relevant work experience or other
              experience, education, transcripts, or other information you provide to us in support
              of an application and/or the application and recruitment process;
            </li>
            <li>Information from interviews and phone-screenings you may have, if any;</li>
            <li>
              Details of the type of employment you are or may be looking for, current and/or
              desired salary and other terms relating to compensation and benefits packages,
              willingness to relocate, or other job preferences;
            </li>
            <li>Details of how you heard about the position you are applying for;</li>
            <li>
              Any sensitive and/or demographic information obtained during the application or
              recruitment process such as gender, information about your citizenship and/or
              nationality, medical or health information and/or your racial or ethnic origin;
            </li>
            <li>
              Reference information and/or information received from background checks (where
              applicable), including information provided by third parties;
            </li>
            <li>
              Information relating to any previous applications you may have made to Moda and/or any
              previous employment history with Moda;
            </li>
            <li>
              Information about your background from publicly available sources, including online,
              that we believe is relevant to your application or a potential future application
              (e.g. your LinkedIn profile); and/or
            </li>
            <li>
              Information related to any assessment you may take as part of the interview screening
              process.
            </li>
          </ul>
        </Text>
        <Text treatment="h5" family="serif">
          How We Use Information We Collect
        </Text>
        <Text treatment="body2" family="serif">
          Your information will be used by us for the purposes of carrying out our application and
          recruitment process, which includes:
        </Text>
        <Text treatment="body2" family="serif">
          <ul className="PrivacyApplicantCandidatePage__list">
            <li>
              Assessing your skills, qualifications, and interests against our career opportunities;
            </li>
            <li>
              Verifying your information and carrying out reference checks and/or conducting
              background checks (where applicable) if you are or may be offered a job;
            </li>
            <li>
              Communications with you about the recruitment process and/or your application(s),
              including, in appropriate cases, informing you of other potential career opportunities
              at Moda;
            </li>
            <li>
              Creating and/or submitting reports as required under any local laws and/or
              regulations, where applicable;
            </li>
            <li>
              Where requested by you and if applicable, assisting you with obtaining an immigration
              visa or work permit where required;
            </li>
            <li>
              Making improvements to our application and/or recruitment process, including improving
              diversity in recruitment practices;
            </li>
            <li>
              Complying with applicable laws, regulations, legal processes or enforceable
              governmental requests; and/or Proactively conducting research about your educational
              and professional background and skills and contacting you if we think you would be
              suitable for a role with us.
            </li>
          </ul>
        </Text>
        <Text treatment="body2" family="serif">
          We will also use your information to protect the rights and property of Moda, our
          customers, applicants, candidates, employees or the public, as required or permitted by
          law.
        </Text>
        <Text treatment="body2" family="serif">
          If you are offered and accept employment with us, the information collected during the
          application and recruitment process will become part of your employment record.
        </Text>
        <Text treatment="h5" family="serif">
          If EU Data Protection Or UK Data Protection Law Applies To The Processing Of Your
          Information, Our Legal Basis For Processing Your Information
        </Text>
        <Text treatment="body2" family="serif">
          We collect and process your information where it is necessary in order to take steps, at
          your request, prior to our potentially entering into a contract of employment with you.
        </Text>
        <Text treatment="body2" family="serif">
          We may also seek your consent to process your personal information in specific
          circumstances, or process it where necessary to comply with a legal obligation or for
          purposes connected to legal claims. If we use your information to improve our application
          or recruitment process, we do so on the basis that it is in our legitimate interests to
          ensure we recruit the best possible candidates.
        </Text>
        <Text treatment="h5" family="serif">
          Who May Have Access To Your Information
        </Text>
        <Text treatment="body2" family="serif">
          <ul className="PrivacyApplicantCandidatePage__list">
            <li>
              Your information may be shared with our affiliates, subsidiaries or joint ventures in
              the United States and in other jurisdictions, in relation to the purposes described
              above. If you have been referred for a job at Moda by a current Moda employee, we may
              inform that employee about the progress of your application and let that employee know
              the outcome of the process. In some cases, if it is identified that you have attended
              the same university/school or shared the same previous employer during the same period
              as a current Moda employee we may consult with that employee for feedback on you.{' '}
            </li>
            <li>
              We may also use service providers acting on our behalf to perform some of the services
              described above, including for the purposes of the verification/background checks.
              These service providers may be located outside the country in which you live or the
              country where the position you have applied for is located.{' '}
            </li>
            <li>
              It may be necessary or advisable for us to disclose your information to external third
              parties such as to local labor authorities, courts and tribunals, regulatory bodies
              and/or law enforcement agencies for the purpose of complying with applicable laws and
              regulations, or in response to legal process.{' '}
            </li>
            <li>
              We will also share your personal information with other third parties if we have your
              consent (for example if you have given us permission to contact your references), or
              to detect, prevent or otherwise address fraud, security or technical issues, or to
              protect against harm to the rights, property or safety of Moda, our customers,
              applicants, candidates, employees or the public or as otherwise required by law.{' '}
            </li>
            <li>
              It is your responsibility to obtain consent from your references before providing
              their personal information to Moda, and you represent and warrant to us that we may
              contact any such references in the ordinary course of your application and/or hiring
              process.
            </li>
          </ul>
        </Text>
        <Text treatment="body2" family="serif">
          We take appropriate steps to protect information about you that is collected, processed,
          and stored as part of the application and recruitment process.
        </Text>
        <Text treatment="h5" family="serif">
          Retention And Updates To Your Information
        </Text>
        <Text treatment="body2" family="serif">
          If you apply for a job at Moda and your application is unsuccessful (or you withdraw from
          the process or decline our offer), we will retain your information for a period after your
          application. We retain this information for various reasons, including in case we face a
          legal challenge in respect of a recruitment decision, to consider you for other current or
          future jobs at Moda and to help us better understand, analyze and improve our recruitment
          processes.
        </Text>
        <Text treatment="body2" family="serif">
          If you do not want us to retain your information for consideration for other roles, or
          want us to update it, please contact{' '}
          <a className="TermsAndConditionsPage__link" href="mailto:legal@modaoperandi.com">
            legal@modaoperandi.com
          </a>
          . Please note, however, that we may retain some information if required by law or as
          necessary to protect ourselves from legal claims.
        </Text>
        <Text treatment="h5" family="serif">
          Your Rights In Respect Of Your Information
        </Text>
        <Text treatment="body2" family="serif">
          In certain countries, you may have certain rights under data protection law. This may
          include the right to request access or to update your information, request that it be
          deleted, or object to Moda using it for certain purposes.
        </Text>
        <Text treatment="body2" family="serif">
          If you wish to exercise a right of access please contact legal@modaoperandi.com. We will
          respond to any requests in accordance with applicable law, and so there may be
          circumstances where we are not able to comply with your request.
        </Text>
        <Text treatment="h5" family="serif">
          Changes To This Policy
        </Text>
        <Text treatment="body2" family="serif">
          We may change this policy from time to time, as posted to this policy on this page. Each
          version of this policy is identified by its effective date.
        </Text>
        <Text treatment="body2" family="serif">
          Last updated: August 30, 2022
        </Text>
      </Stack>
    </div>
  </>
);
